// The public shared-trip view: /shared/trip/:shareId, a sibling of /support,
// NOT under /me -- anyone holding the link can open it with no account.
//
// Like /salish, nothing on these pages reads the database: every table is
// userPool-authed and would come back silently empty for a signed-out
// visitor. The one fetch goes to the share-trip Lambda's public Function URL
// (src/utilities/shareTrip.mjs), which validates the unguessable share id and
// returns {name, legs, places} -- the places being the gazetteer rows the
// routes reference, since the anonymous page can't look them up itself. The
// weather layers on the maps need none of this: that stack is already fully
// public (client-direct APIs plus the weather-proxy Function URL).
//
// This component owns the fetch and the derived display state; the itinerary
// and the per-leg view are nested routes sharing it via Outlet context, so a
// deep link to a leg works and stepping into a leg costs no second fetch.
//
// Read-only is the contract: no edit control renders anywhere under this
// route, and the resolved display copies are never written back.

import { useState, useEffect, useMemo } from 'react';
import { useParams, Outlet, useOutletContext } from 'react-router-dom';
import { Spinner } from '../../components';
import { fetchSharedTrip, shareTripAvailable } from '../../utilities/shareTrip.mjs';
import { indexPlaces } from '../../utilities/places.mjs';
import { tripFields, tripListColumns, sortTripsAscending } from '../../fleet-shared/TripFields.mjs';
import { groupTrips } from '../../fleet-shared/TripGroups.mjs';
import { tripWithResolvedEndpoints } from '../../fleet-shared/TripRoute.mjs';
import { placeCell } from '../../fleet-shared/PlaceFields.mjs';
import { weekdayShortDate } from '../../fleet-shared/Common.mjs';
import { useDocumentTitle } from '../../utilities/useDocumentTitle';

// The itinerary columns, shared with the per-leg view so the two pages can't
// disagree on labels or formatting. Same columns as the owner's TripGroup
// table -- minus its Order and Details controls, which is the point.
export const sharedTripColumns = tripListColumns
  .map((key) => tripFields.find((f) => f.key === key))
  .filter(Boolean);

// One cell value, TripGroup's register: dates carry their weekday (these rows
// are the schedule), and a From/To cell shows the gazetteer's short name with
// the full one on `title`.
export function sharedCellValue(leg, field, placeIndex) {
  const value = leg[field.key];
  if (field.type === 'date') return { text: weekdayShortDate(value ?? null) };
  if (value === null || value === undefined) return { text: '' };
  if (field.type === 'place') return placeCell(value, placeIndex);
  return { text: value };
}

export function useSharedTrip() {
  return useOutletContext();
}

const SharedTrip = () => {
  const { shareId } = useParams();

  // 'loading' | 'gone' | 'error' | 'ok'
  const [status, setStatus] = useState('loading');
  const [payload, setPayload] = useState(null);

  useEffect(() => {
    // A build without the share backend can still be handed a share URL;
    // "gone" is the honest answer there too (nothing can be looked up).
    if (!shareTripAvailable()) {
      setStatus('gone');
      return undefined;
    }
    let mounted = true;
    setStatus('loading');
    setPayload(null);
    fetchSharedTrip(shareId)
      .then((result) => {
        if (!mounted) return;
        if (result.status === 'gone') {
          setStatus('gone');
        } else {
          setPayload(result);
          setStatus('ok');
        }
      })
      .catch(() => {
        if (mounted) setStatus('error');
      });
    return () => { mounted = false; };
  }, [shareId]);

  // The returned place rows, indexed the way every resolver expects.
  const placeIndex = useMemo(
    () => indexPlaces(payload?.places ?? []),
    [payload]
  );

  // Display copies, exactly as the owner's TripGroup builds them: routes
  // resolved against the (returned slice of the) gazetteer, From/To re-derived
  // from the resolved route. Never written anywhere.
  const displayLegs = useMemo(() => {
    if (!payload) return null;
    return sortTripsAscending(payload.legs).map((leg) =>
      tripWithResolvedEndpoints(leg, placeIndex)
    );
  }, [payload, placeIndex]);

  // All legs share one group by construction (the Lambda filtered to it);
  // grouping the display copies recomputes name/dates/summary with the place
  // index applied. The Lambda's name is the fallback for safety's sake.
  const group = useMemo(() => {
    if (!displayLegs) return null;
    return groupTrips(displayLegs, { placeIndex })[0] ?? null;
  }, [displayLegs, placeIndex]);

  const name = group?.name || payload?.name || 'Trip';

  useDocumentTitle(status === 'ok' ? `${name} - TaskPogo` : 'Shared Trip - TaskPogo');

  if (status === 'loading') {
    return <Spinner />;
  }

  if (status === 'gone') {
    return (
      <p className="text-center text-body-secondary py-5">
        This shared trip is no longer available.
      </p>
    );
  }

  if (status === 'error') {
    return (
      <p className="text-center text-body-secondary py-5">
        We couldn&apos;t load this trip. Please try again later.
      </p>
    );
  }

  return (
    <div className="pb-2">
      <Outlet context={{ shareId, name, group, displayLegs, placeIndex }} />
    </div>
  );
};

export default SharedTrip;
